import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError, imgUrl } from "@/lib/api";
import { toast } from "sonner";
import { AutomationsSubnav, PageIntro } from "@/components/SellerToolsNav";
import { TILES } from "@/components/ConnectPrimaryCard";

export default function Offers() {
  const qc = useQueryClient();
  const [picked, setPicked] = useState({});
  const [pct, setPct] = useState(10);
  const [channel, setChannel] = useState("shoptosa");
  const [preview, setPreview] = useState([]);
  const { data, isLoading } = useQuery({
    queryKey: ["seller-offers"],
    queryFn: async () => (await api.get("/seller/offers")).data,
  });
  const items = data?.candidates || [];
  const ids = useMemo(() => Object.keys(picked).filter((id) => picked[id]), [picked]);

  const runPreview = async () => {
    if (!ids.length) return toast.error("Select listings");
    try {
      const { data: res } = await api.post("/seller/offers/preview", { product_ids: ids, discount_pct: pct, channel });
      setPreview(res.offers || []);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const send = async () => {
    if (!ids.length) return toast.error("Select listings");
    try {
      const { data: res } = await api.post("/seller/offers/send", { product_ids: ids, discount_pct: pct, channel, mode: "manual" });
      toast.success(channel === "shoptosa" ? `Applied ${res.offers.length} shop discount${res.offers.length === 1 ? "" : "s"}` : `${res.offers.length} pack${res.offers.length === 1 ? "" : "s"} ready to copy`);
      setPreview(res.offers || []);
      qc.invalidateQueries({ queryKey: ["seller-offers"] });
      qc.invalidateQueries({ queryKey: ["crosslist-items"] });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const copyOffer = async (offer) => {
    await navigator.clipboard.writeText(offer.message);
    try {
      await api.post(`/seller/offers/${offer.id}/copied`);
    } catch { /* preview rows may not be saved */ }
    toast.success("Copied");
  };

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10 pt-28 pb-24" data-testid="seller-offers">
      <AutomationsSubnav />
      <PageIntro
        kicker="Automations"
        title="Send Offers"
        blurb="Select listings and a discount. On Shoptosa the shop price updates. On other marketplaces you copy the offer text — we do not send to likers on eBay, Poshmark, Mercari, or Depop."
      />
      <div className="flex flex-wrap gap-3 mb-6">
        <label className="text-sm">
          Discount %
          <input type="number" min="1" max="80" value={pct} onChange={(e) => setPct(Number(e.target.value))} className="ml-2 w-20 px-2 py-1.5 border border-aura-border rounded-lg bg-aura-surface" />
        </label>
        <label className="text-sm">
          Channel
          <select value={channel} onChange={(e) => setChannel(e.target.value)} className="ml-2 px-2 py-1.5 border border-aura-border rounded-lg bg-aura-surface">
            <option value="shoptosa">Shoptosa Shop</option>
            {TILES.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </label>
        <button type="button" onClick={runPreview} className="px-3 py-1.5 text-sm border border-aura-border rounded-lg">Preview</button>
        <button type="button" onClick={send} className="px-3 py-1.5 text-sm bg-aura-accent text-white rounded-lg">Send / queue</button>
      </div>
      {isLoading ? <div className="h-32 shimmer rounded-lg" /> : (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="border border-aura-border rounded-lg divide-y divide-aura-border max-h-[520px] overflow-auto">
            {items.map((p) => (
              <label key={p.id} className="flex items-center gap-3 p-3 text-sm">
                <input type="checkbox" checked={!!picked[p.id]} onChange={(e) => setPicked((s) => ({ ...s, [p.id]: e.target.checked }))} />
                <img src={imgUrl(p.images?.[0])} alt="" className="w-10 h-12 object-cover rounded" />
                <span className="truncate flex-1">{p.title}</span>
                <span className="font-mono text-aura-muted">${p.price}</span>
              </label>
            ))}
          </div>
          <div>
            <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-2">Preview</div>
            {preview.length === 0 ? <div className="text-sm text-aura-muted">Preview to see the discounted price and copy pack.</div> : preview.map((o, i) => (
              <div key={o.id || i} className="border border-aura-border rounded-lg p-3 mb-3 text-sm">
                <div className="font-medium">{o.title}</div>
                <div className="font-mono text-xs text-aura-muted mt-1">{o.original_price} → {o.offer_price} ({o.discount_pct}%)</div>
                <p className="text-xs mt-2 whitespace-pre-wrap">{o.message}</p>
                {o.channel !== "shoptosa" && (
                  <button type="button" onClick={() => copyOffer(o)} className="mt-2 text-xs px-3 py-1 border border-aura-border rounded-lg">Copy pack</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {(data?.offers || []).length > 0 && (
        <div className="mt-10">
          <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-2">Recent offers</div>
          {(data.offers || []).slice(0, 12).map((o) => (
            <div key={o.id} className="text-xs text-aura-muted py-1 font-mono">{o.status} · {o.channel} · {o.title}</div>
          ))}
        </div>
      )}
    </div>
  );
}
